import {
  Box,
  Menu,
  Paper,
  MenuItem,
  MenuList,
  Typography,
  ListItemIcon,
  ListItemText,
  hexToRgb,
  IconButton,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import Tooltip from '@mui/material/Tooltip';
import PhotoSizeSelectLargeIcon from '@mui/icons-material/PhotoSizeSelectLarge';
import { useDispatch, useSelector } from 'react-redux';
import LockIcon from '@mui/icons-material/Lock';
import { ExpertContent, UrlType } from 'pages/media-library/interface';
import * as React from 'react';
import { selectSelectedFiles, selectSelectedTargetFolder } from 'pages/media-library/selector';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  BoldAudioIcon,
  BoldExpandMoreIcon, BoldFolderIcon, BoldPlayIcon, BoldShareIcon, EditIcon, TrashIcon,
} from 'assets/icons';
import PublicIcon from '@mui/icons-material/Public';
import { getFileSize } from 'utils/formats';
import { FileType, urlExists, getDocumentIcon } from 'utils/file-uploader';
import ShareModal from 'components/organisms/share-modal';
import { getUserHubUrl } from 'utils/common-utils';
import { GenerateVideoThumbnail, useConfirmation } from 'base/common';
import { ContentCut, FileDownload } from '@mui/icons-material';
import { selectEnterprise } from 'base/core/selectors';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { isYouTubeUrl, extractYouTubeVideoId, getYouTubeThumbnailUrl } from 'utils/youtube-utils';
import YouTubeModal from 'components/organisms/youtube-modal';
import youtubeIcon from 'assets/images/youtube-icon.png';
import { convertToHLSUrl } from 'components/organisms/streaming-player/utils';
import folderImage from 'assets/images/folder.png';
import { mediaLibraryAction } from '../../slice';

export interface MediaItemProps {
  media: ExpertContent;
  direction?: 'column' | 'row';
  downloadSingleFile: (file: ExpertContent) => void;
  copyLink: (link: ExpertContent) => void;
  openTrimModel: (media: ExpertContent) => void;
  setEditDetails: (media: ExpertContent) => void;
}

const MediaItem: React.FC<MediaItemProps> = ({
  media, direction, downloadSingleFile, copyLink, openTrimModel, setEditDetails,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const dispatch = useDispatch();
  const { entLogo } = useSelector(selectEnterprise);
  const selectedFiles = useSelector(selectSelectedFiles);
  const selectedTargetFolder = useSelector(selectSelectedTargetFolder);
  const location = useLocation();
  const navigate = useNavigate();
  const userHubUrl = getUserHubUrl();
  const confirmAlert = useConfirmation();
  const {
    _id: mediaId, title: mediaTitle, fileCategory, fileType, thumbnail,
    urlType, contentUrl: mediaUrl, fileSize, isExternalFile, isPublic, path,
  } = media;

  const isSelected = React.useMemo(
    () => selectedFiles.includes(mediaId),
    [selectedFiles, mediaId],
  );

  const [openShareMedia, setOpenShareMedia] = React.useState(false);
  const [isDragEntered, setIsDragEntered] = React.useState(false);
  const [openThumbnailModal, setOpenThumbnailModal] = React.useState({
    open: false,
    mediaId: '',
    mediaUrl: '',
    mediaExt: '',
  });
  const [openYouTubeModal, setOpenYouTubeModal] = React.useState(false);
  const [click, setClick] = React.useState(false);
  const [processing, setProcessing] = React.useState(false);

  React.useEffect(() => {
    const checkProcessing = async () => {
      try {
        const convertedSrc = await convertToHLSUrl(mediaUrl);
        const isProcessed = convertedSrc.endsWith('.m3u8');
        setProcessing(!isProcessed);
        return isProcessed; // Return true if processing is complete
      } catch (error) {
        // In case of error, consider available the raw file
        setProcessing(false);
        return false;
      }
    };

    let interval: NodeJS.Timeout;

    if (fileCategory === 'video' && !isExternalFile && mediaUrl) {
      // Initial check
      checkProcessing().then((isProcessed) => {
        if (isProcessed) return; // Stop if already processed

        // Poll every 5 seconds if still processing
        interval = setInterval(async () => {
          const isAlreadyProcessed = await checkProcessing();
          if (isAlreadyProcessed) {
            clearInterval(interval); // Stop polling when m3u8 is detected
          }
        }, 5000);
      });
    } else {
      setProcessing(false);
    }

    return () => clearInterval(interval);
  }, [fileCategory, isExternalFile, mediaUrl]);

  const handleShareMediaOpen = () => setOpenShareMedia(true);
  const handleShareMediaClose = () => {
    setOpenShareMedia(false);
    dispatch(mediaLibraryAction.updateMedia({
      id: mediaId,
      isPublic: true,
    }));
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDragStart = (event: React.DragEvent<HTMLDivElement>) => {
    let updatedSelectedFiles = selectedFiles;

    const isMultiple = selectedFiles.length > 1;

    if (!selectedFiles.includes(media._id)) {
      updatedSelectedFiles = [...selectedFiles, media._id];
      dispatch(mediaLibraryAction.toggleSelectedFile(media._id));
      event.dataTransfer.setData('application/json', JSON.stringify([media._id]));
    } else {
      event.dataTransfer.setData('application/json', JSON.stringify(selectedFiles));
    }

    // eslint-disable-next-line no-param-reassign
    event.dataTransfer.effectAllowed = 'move';

    if (isMultiple) {
      const dragPreview = document.createElement('div');
      dragPreview.style.position = 'absolute';
      dragPreview.style.top = '-999px';
      dragPreview.style.left = '-999px';
      dragPreview.style.padding = '8px 12px';
      dragPreview.style.background = 'rgba(0,0,0,0.7)';
      dragPreview.style.color = 'white';
      dragPreview.style.borderRadius = '4px';
      dragPreview.style.fontSize = '14px';
      dragPreview.style.display = 'flex';
      dragPreview.style.alignItems = 'center';
      dragPreview.style.justifyContent = 'center';
      dragPreview.style.flexDirection = 'column';

      const icon = document.createElement('img');
      icon.src = folderImage;
      icon.alt = 'icon';
      icon.style.width = '50px';
      icon.style.height = '50px';

      const text = document.createElement('span');
      text.innerText = `${updatedSelectedFiles.length} items`;

      dragPreview.appendChild(icon);
      dragPreview.appendChild(text);

      document.body.appendChild(dragPreview);

      event.dataTransfer.setDragImage(dragPreview, 0, 0);

      setTimeout(() => document.body.removeChild(dragPreview), 0);
    } else {
      event.dataTransfer.setDragImage(event.target as HTMLElement, 0, 0);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    dispatch(mediaLibraryAction.deselectFilesSelectedForUndo());
    if (selectedTargetFolder !== null && selectedFiles.length > 0 && isDragEntered) {
      dispatch(mediaLibraryAction.moveMedia({ isDragged: true }));
    } else {
      dispatch(mediaLibraryAction.deselectAllFiles());
      dispatch(mediaLibraryAction.setSelectedTargetFolder(null));
      setIsDragEntered(false);
    }
  };

  const handleDragLeave = () => {
    setIsDragEntered(false);
    dispatch(mediaLibraryAction.setSelectedTargetFolder(null));
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (selectedFiles.includes(media._id) || media.fileCategory !== FileType.FOLDER) {
      return;
    }
    setIsDragEntered(true);
    dispatch(mediaLibraryAction.setSelectedTargetFolder({
      id: media._id,
      path: media.path ?? '/root',
    }));
  };

  const onDeleteMediaHanlder = () => {
    confirmAlert({
      variant: 'confirm',
      icon: 'warning',
      message: 'Are you sure you want to delete this media?',
      confirmButtonTxt: 'Yes',
      cancelButtonTxt: 'Cancel',
    })
      .then(() => {
        dispatch(
          mediaLibraryAction.deleteMedia({
            id: mediaId,
            contentUrl: mediaUrl || null,
            fileSize: +fileSize,
          }),
        );
      });
  };

  const onMoveMediaHanlder = () => {
    dispatch(mediaLibraryAction.setOpenFolderSelect(true));
    dispatch(mediaLibraryAction.fetchFolderSelectorMedia());
    dispatch(mediaLibraryAction.selectMediaItem({ id: mediaId, path: media.path ?? '', title: media.title }));
    handleClose();
  };

  const onEditMediaHanlder = () => {
    setEditDetails(media);
    dispatch(mediaLibraryAction.setRenameMediaFormOpen(true));
    setEditDetails(media);
  };

  const onShareMediaHandler = async () => {
    confirmAlert({
      variant: 'confirm',
      icon: 'warning',
      message: 'Sharing this media will make it public. Are you sure?',
      confirmButtonTxt: 'Yes',
      cancelButtonTxt: 'Cancel',
    })
      .then(() => {
        handleShareMediaOpen();
      });
  };

  const onChangePublicMediaHandler = () => {
    dispatch(mediaLibraryAction.updateMedia({
      id: mediaId,
      isPublic: false,
    }));
  };

  const handleToggle = () => {
    dispatch(mediaLibraryAction.toggleSelectedFile(mediaId));
    dispatch(mediaLibraryAction.toggleSelectedFilesForUndo({ id: mediaId, path: path ?? '', title: media.title }));
  };

  const onMediaClickHandler = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();

    if (processing) return;

    if (fileCategory === FileType.FOLDER) {
      navigate(`${location.pathname}/${mediaTitle}`);
      return;
    }

    if (isYouTubeUrl(mediaUrl)) {
      setOpenYouTubeModal(true);
      return;
    }

    if (typeof window !== undefined) {
      window.open(mediaUrl, '_blank');
    }
  };

  const mediaThumbnail: string = React.useMemo(() => {
    if (fileCategory === 'image') {
      return mediaUrl;
    }

    if (fileCategory === 'video') {
      if (isYouTubeUrl(mediaUrl)) {
        const videoId = extractYouTubeVideoId(mediaUrl);
        if (videoId) {
          return getYouTubeThumbnailUrl(videoId, 'medium');
        }
      }

      if (thumbnail !== '' && urlExists(thumbnail)) {
        return thumbnail;
      }
    }

    if (fileCategory === 'document') {
      return getDocumentIcon(fileType || '.pdf');
    }

    return entLogo;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaId, entLogo, fileType, mediaUrl]);

  return (
    <>
      <ShareModal
        open={openShareMedia}
        onClose={handleShareMediaClose}
        modalTitle="Share this session"
        shareTitle={mediaTitle}
        shareUrl={`${userHubUrl}/experts/media/shared/${mediaId}`}
      />
      {openThumbnailModal.open && openThumbnailModal.mediaUrl && openThumbnailModal.mediaUrl !== '' && (
        <GenerateVideoThumbnail
          open={openThumbnailModal.open}
          onClose={() => setOpenThumbnailModal({ ...openThumbnailModal, open: false })}
          videoUrl={openThumbnailModal.mediaUrl}
          videoExt={openThumbnailModal.mediaExt}
          onFileCreate={({ fileName, url }) => {
            dispatch(mediaLibraryAction.updateMedia({
              id: openThumbnailModal.mediaId,
              thumbnailGenerated: fileName,
            }));
          }}
        />
      )}
      <YouTubeModal
        open={openYouTubeModal}
        onClose={() => setOpenYouTubeModal(false)}
        url={mediaUrl}
        title={mediaTitle}
      />
      <Box
        draggable
        onDragStart={handleDragStart}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        sx={{
          display: 'flex',
          '> div': { flex: direction === 'column' ? 'auto' : 1 },
          flexDirection: direction,
          boxShadow: (t) => t.custom.boxShadow,
          background: '#fff',
          border: (t) => (isDragEntered && `3px solid ${t.palette.primary.main}`)
            || (isSelected && `2px solid ${t.palette.primary.dark}`)
            || `2px solid ${t.palette.background.default}`,
          borderRadius: (t) => `calc(${t.custom.borderRadius} + 2px)`,
          '&:hover': {
            '.media-item-name': {
              color: (t) => t.palette.primary.main,
            },
            '.media-item-overlay': {
              background: (t) => {
                let color = hexToRgb(t.palette.primary.main);
                color = color.replace('rgb', 'rgba');
                color = color.replace(')', ', 0.1)');
                return color;
              },
            },
          },
        }}
      >
        <Box
          display="flex"
          sx={{
            position: 'relative',
            borderTopLeftRadius: (t) => t.custom.borderRadius,
            borderTopRightRadius: (t) => (direction === 'column' ? t.custom.borderRadius : 0),
            borderBottomLeftRadius: (t) => (direction === 'row' ? t.custom.borderRadius : 0),
          }}
          height={125}
          alignItems="center"
          flexDirection="column"
          justifyContent="center"
        >
          {fileCategory === 'image' && (
            <Box
              component="img"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderTopLeftRadius: (t) => t.custom.borderRadius,
                borderTopRightRadius: (t) => (direction === 'column' ? t.custom.borderRadius : 0),
                borderBottomLeftRadius: (t) => (direction === 'row' ? t.custom.borderRadius : 0),
              }}
              src={mediaThumbnail}
            />
          )}

          {fileCategory === 'video' && thumbnail !== '' && (
            <Box
              component="img"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderTopLeftRadius: (t) => t.custom.borderRadius,
                borderTopRightRadius: (t) => (direction === 'column' ? t.custom.borderRadius : 0),
                borderBottomLeftRadius: (t) => (direction === 'row' ? t.custom.borderRadius : 0),
                opacity: processing ? 0.5 : 1,
                filter: processing ? 'grayscale(100%)' : 'none',
              }}
              src={mediaThumbnail}
            />
          )}

          {fileCategory === 'document' && (
            <Box
              component="img"
              sx={{
                width: '50%',
                height: 'auto',
                objectFit: 'cover',
                borderTopLeftRadius: (t) => t.custom.borderRadius,
                borderTopRightRadius: (t) => (direction === 'column' ? t.custom.borderRadius : 0),
                borderBottomLeftRadius: (t) => (direction === 'row' ? t.custom.borderRadius : 0),
              }}
              src={mediaThumbnail}
            />
          )}

          <Box
            className="media-item-overlay"
            sx={{
              position: 'absolute',
              inset: 0,
              transition: 'background 0.3s ease-in-out',
              borderTopLeftRadius: (t) => t.custom.borderRadius,
              borderTopRightRadius: (t) => t.custom.borderRadius,
              background: (t) => {
                let color = hexToRgb(t.palette.primary.main);
                color = color.replace('rgb', 'rgba');

                if (
                  fileCategory === FileType.FOLDER
                  || fileCategory === FileType.AUDIO
                  || fileCategory === FileType.DOCUMENT
                ) {
                  color = color.replace(')', ', 0.1)');
                }

                if (
                  fileCategory === FileType.IMAGE
                  || fileCategory === FileType.VIDEO
                ) {
                  color = color.replace(')', ', 0.3)');
                }

                return color;
              },
            }}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Box sx={{ position: 'absolute', top: 1, right: 1 }}>
              <IconButton onClick={handleClick}>
                <BoldExpandMoreIcon
                  sx={{
                    fontSize: 14,
                    color: (t) => {
                      if (
                        fileCategory === 'folder'
                        || fileCategory === 'document'
                      ) {
                        return t.palette.primary.main;
                      }

                      return t.palette.common.white;
                    },
                  }}
                />
              </IconButton>
            </Box>
            {!processing && (
              <Box sx={{ position: 'absolute', top: 1, left: 9 }}>
                <FormGroup>
                  <FormControlLabel
                    control={<Checkbox size="small" />}
                    label=""
                    checked={isSelected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={handleToggle}
                    sx={{
                      '.MuiTypography-body1': {
                        color: (t) => t.palette.grey.A100,
                        fontWeight: 'medium',
                      },
                    }}
                  />
                </FormGroup>
              </Box>
            )}

            {fileCategory === 'video' && (
              <BoldPlayIcon
                sx={{ color: (t) => t.palette.common.white, fontSize: 30 }}
              />
            )}

            {fileCategory === 'folder' && (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                width={50}
                height={50}
                sx={{
                  background: (t) => t.palette.grey.A100,
                  borderRadius: '50%',
                }}
              >
                <BoldFolderIcon
                  sx={{ color: (t) => t.palette.primary.main }}
                />
              </Box>
            )}

            {fileCategory === 'audio' && (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                width={50}
                height={50}
                sx={{
                  background: (t) => t.palette.grey.A100,
                  borderRadius: '50%',
                }}
              >
                <BoldAudioIcon
                  sx={{ color: (t) => t.palette.primary.main }}
                />
              </Box>
            )}
          </Box>
        </Box>

        <Paper sx={{ p: 2, borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
          <Tooltip
            title={mediaTitle}
            disableHoverListener={!mediaTitle || mediaTitle.length < 20}
          >
            <Typography
              variant="body2"
              className="media-item-name"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                cursor: processing ? 'default' : 'pointer',
              }}
              onClick={onMediaClickHandler}
            >
              {mediaTitle}
            </Typography>
          </Tooltip>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
            {processing ? 'Processing...' : 'Uploaded by You'}
          </Typography>
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
            {!isExternalFile && !processing && (
              <div className="col-8 col-md-8 col-lg-5 col-xl-9">
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {fileCategory !== FileType.FOLDER && `${getFileSize(fileSize)}`}
                  {fileCategory === FileType.FOLDER && 'Folder'}
                </Typography>
              </div>
            )}
            {isPublic && (
              <div className="col-4 col-md-4 col-lg-5 col-xl-3">
                <Box>
                  <PublicIcon color="primary" fontSize="small" sx={{ mt: '5px', mb: '-5px' }} />
                </Box>
              </div>
            )}
            {fileCategory !== 'folder' && !processing && (
              <div className="col-4 col-md-4 col-lg-5 col-xl-3">
                {!isExternalFile ? (
                  <IconButton disableRipple sx={{ p: 0 }} onClick={() => downloadSingleFile(media)}>
                    <FileDownload sx={{ color: (t) => t.palette.primary.main }} fontSize="small" />
                  </IconButton>
                ) : (
                  <IconButton
                    disableRipple
                    disableFocusRipple
                    sx={{ p: 0 }}
                    onClick={() => {
                      copyLink(media);
                      setClick(true);
                      setTimeout(() => {
                        setClick(false);
                      }, 2000);
                    }}
                  >
                    {click ? <Tooltip title="Copied!" arrow><CheckRoundedIcon color="primary" fontSize="small" /></Tooltip> : <Tooltip title="Copy Link to Clipboard" arrow><LinkIcon color="primary" fontSize="small" /></Tooltip>}
                  </IconButton>
                )}
              </div>
            )}

            {urlType === UrlType.YOUTUBE_LINK && (
              <img
                src={youtubeIcon}
                alt="YouTube Icon"
                style={{
                  width: 'auto', height: '25px',
                }}
              />
            )}
          </div>
        </Paper>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        sx={{ ul: { width: 187 } }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuList>
          <MenuItem onClick={onEditMediaHanlder}>
            <ListItemIcon>
              <EditIcon
                sx={{ fontSize: 20, color: (t) => t.palette.primary.main }}
              />
            </ListItemIcon>
            <ListItemText>
              <Typography fontSize={{ xs: 14 }}>Rename</Typography>
            </ListItemText>
          </MenuItem>
          <MenuItem onClick={onMoveMediaHanlder}>
            <ListItemIcon>
              <BoldFolderIcon
                sx={{ fontSize: 20, color: (t) => t.palette.primary.main }}
              />
            </ListItemIcon>
            <ListItemText>
              <Typography fontSize={{ xs: 14 }}>Move</Typography>
            </ListItemText>
          </MenuItem>
          <MenuItem onClick={onDeleteMediaHanlder}>
            <ListItemIcon>
              <TrashIcon
                sx={{ fontSize: 20, color: (t) => t.palette.primary.main }}
              />
            </ListItemIcon>
            <ListItemText>
              <Typography fontSize={{ xs: 14 }}>Delete</Typography>
            </ListItemText>
          </MenuItem>
          {fileCategory !== 'folder' && !processing && (
            <MenuItem onClick={onShareMediaHandler}>
              <ListItemIcon>
                <BoldShareIcon
                  sx={{ fontSize: 20, color: (t) => t.palette.primary.main }}
                />
              </ListItemIcon>
              <ListItemText>
                <Typography fontSize={{ xs: 14 }}>Share</Typography>
              </ListItemText>
            </MenuItem>
          )}
          {isPublic && fileCategory !== 'folder' && !processing && (
            <MenuItem onClick={onChangePublicMediaHandler}>
              <ListItemIcon>
                <LockIcon
                  sx={{ fontSize: 20, color: (t) => t.palette.primary.main }}
                />
              </ListItemIcon>
              <ListItemText>
                <Typography fontSize={{ xs: 14 }}>Make Private</Typography>
              </ListItemText>
            </MenuItem>
          )}
          {fileCategory === 'video' && !processing && urlType !== UrlType.YOUTUBE_LINK && (
            <MenuItem onClick={() => setOpenThumbnailModal({
              open: true, mediaUrl, mediaId, mediaExt: fileType.replace('.', '').toLowerCase(),
            })}
            >
              <ListItemIcon>
                <PhotoSizeSelectLargeIcon
                  sx={{ fontSize: 20, color: (t) => t.palette.primary.main }}
                />
              </ListItemIcon>
              <ListItemText>
                <Typography fontSize={{ xs: 14 }}>Generate thumbnail</Typography>
              </ListItemText>
            </MenuItem>
          )}
          {!isExternalFile && fileCategory === 'video' && !processing && (
            <MenuItem onClick={() => openTrimModel(media)}>
              <ListItemIcon>
                <ContentCut
                  sx={{ fontSize: 20, color: (t) => t.palette.primary.main }}
                />
              </ListItemIcon>
              <ListItemText>
                <Typography fontSize={{ xs: 14 }}>Trim Video</Typography>
              </ListItemText>
            </MenuItem>
          )}
        </MenuList>
      </Menu>
    </>
  );
};

MediaItem.defaultProps = {
  direction: 'column',
};
export default MediaItem;
